import React, { useEffect, useState, useMemo } from 'react';
import {
  Tabs,
  Tooltip,
  message,
  Modal,
  Form,
  Input,
  Select,
  Divider,
  Tag,
  Table,
  Button,
  Popconfirm,
} from 'antd';
import { withRouter, useParams } from 'react-router-dom';
import {
  CrownOutlined,
  UserOutlined,
  TeamOutlined,
  SwapOutlined,
  UserDeleteOutlined,
  InboxOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import AppOverlaySpinner from '../shared/AppOverlaySpinner';
import TeamDetailsHeader from './TeamDetailsHeader';
import TeamDetailsStats from './TeamDetailsStats';
import TeamTasksList from './TeamTasksList';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { space, radius, text, neutral } from '../../configs/designTokens';
import '../../styles/index.scss';

const { TabPane } = Tabs;
const { Option } = Select;

const TeamDetails = ({ history }) => {
  const { id: teamId } = useParams();
  const { isMobile, isTablet } = useResponsiveTask();

  const {
    fetchTeams,
    teamsData,
    teamsLoading,
    updateTeam,
    updateTeamLoading,
    updateTask,
    fetchAllTasks,
    allTasksData,
    allTasksLoading,
    fetchOrgTeams,
    orgTeamsData,
  } = useTaskContext();

  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();

  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [settingsForm] = Form.useForm();
  const [editingTeam, setEditingTeam] = useState(null);

  // Add members modal
  const [isAddMemberModalVisible, setIsAddMemberModalVisible] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);

  // Delete team modal — Danger Zone
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch team data
  useEffect(() => {
    if (teamId) {
      fetchTeams({
        variables: {
          where: { id: teamId },
        },
      });
      fetchAllTasks({
        variables: {
          where: { teams_some: { id: teamId } },
          orderBy: 'createdAt_DESC',
          first: 1000,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const team = useMemo(() => {
    return (teamsData?.teams || []).find((t) => t.id === teamId) || null;
  }, [teamsData, teamId]);

  const allTasks = useMemo(() => {
    if (!allTasksData?.tasksConnection?.edges) return [];
    return allTasksData.tasksConnection.edges.map((edge) => edge.node);
  }, [allTasksData]);

  const unassignedTasks = useMemo(() => {
    return allTasks.filter((task) => !task.assigneeUsers || task.assigneeUsers.length === 0);
  }, [allTasks]);

  const refetchData = () => {
    if (teamId) {
      fetchTeams({ variables: { where: { id: teamId } } });
      fetchAllTasks({
        variables: {
          where: { teams_some: { id: teamId } },
          orderBy: 'createdAt_DESC',
          first: 1000,
        },
      });
    }
  };

  // User fetching for org
  const fetchUsersForOrg = (name = '') => {
    const orgId = team?.org?.id;
    if (!orgId) return;
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: orgId, level: 'PLATFORM' },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  };

  // ============================================
  // SETTINGS MODAL
  // ============================================

  // All org teams for duplicate name validation
  const allOrgTeams = useMemo(() => {
    return (orgTeamsData?.teams || []).filter((t) => !t.name.startsWith('__deleted__'));
  }, [orgTeamsData]);

  const handleOpenSettings = () => {
    if (!team) return;
    settingsForm.setFieldsValue({
      name: team.name,
      description: team.description || '',
    });
    setEditingTeam({ ...team });
    setIsSettingsModalVisible(true);
    fetchUsersForOrg();
    if (team?.org?.id) {
      fetchOrgTeams({
        variables: {
          where: { org: { id: team.org.id } },
          orderBy: 'name_ASC',
        },
      });
    }
  };

  const handleSettingsSave = async () => {
    try {
      const values = await settingsForm.validateFields();
      await updateTeam({
        variables: {
          data: { name: values.name, description: values.description },
          where: { id: teamId },
        },
      });
      message.success('Team updated successfully');
      setIsSettingsModalVisible(false);
      setEditingTeam(null);
      refetchData();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to update team: ' + error.message);
    }
  };

  const handleTransferLeadership = async (userId) => {
    try {
      await updateTeam({
        variables: {
          data: { leader: { connect: { id: userId } } },
          where: { id: teamId },
        },
      });
      const user = editingTeam?.members?.find((m) => m.id === userId);
      message.success(`Leadership transferred to ${startCase(user?.name || user?.username)}`);
      refetchData();
      setEditingTeam((prev) => ({ ...prev, leader: user }));
    } catch (error) {
      message.error('Failed to transfer leadership: ' + error.message);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (editingTeam?.leader?.id === userId) {
      message.warning('Cannot remove the team leader. Transfer leadership first.');
      return;
    }
    try {
      // Step 1: Unassign user from any team tasks where they're an assignee
      const tasksToUpdate = allTasks.filter((task) => {
        const inMulti = task.assigneeUsers?.some((u) => u.id === userId);
        const isSingle = task.assigneeUser?.id === userId;
        return inMulti || isSingle;
      });
      for (const task of tasksToUpdate) {
        const taskData = {};
        if (task.assigneeUsers?.some((u) => u.id === userId)) {
          taskData.assigneeUsers = { disconnect: [{ id: userId }] };
        }
        if (task.assigneeUser?.id === userId) {
          taskData.assigneeUser = { disconnect: true };
        }
        await updateTask({ variables: { data: taskData, where: { id: task.id } } });
      }

      // Step 2: Remove user from team members
      await updateTeam({
        variables: {
          data: { members: { disconnect: [{ id: userId }] } },
          where: { id: teamId },
        },
      });

      const unassignedCount = tasksToUpdate.length;
      message.success(
        unassignedCount > 0
          ? `Member removed and unassigned from ${unassignedCount} task${unassignedCount === 1 ? '' : 's'}`
          : 'Member removed'
      );
      refetchData();
      setEditingTeam((prev) => ({
        ...prev,
        members: prev.members.filter((m) => m.id !== userId),
      }));
    } catch (error) {
      message.error('Failed to remove member: ' + error.message);
    }
  };

  const handleAddMembers = async () => {
    if (selectedMemberIds.length === 0) {
      message.warning('Please select at least one member to add');
      return;
    }
    try {
      await updateTeam({
        variables: {
          data: { members: { connect: selectedMemberIds.map((id) => ({ id })) } },
          where: { id: teamId },
        },
      });
      const count = selectedMemberIds.length;
      message.success(`${count} member${count > 1 ? 's' : ''} added to team`);
      refetchData();
      const addedUsers = basicUsersDetails.filter((u) => selectedMemberIds.includes(u.id));
      setEditingTeam((prev) => ({
        ...prev,
        members: [...(prev.members || []), ...addedUsers],
      }));
      setIsAddMemberModalVisible(false);
      setSelectedMemberIds([]);
    } catch (error) {
      message.error('Failed to add members: ' + error.message);
    }
  };

  // ============================================
  // DELETE TEAM (Danger Zone)
  // ============================================

  const handleDeleteTeam = async () => {
    if (!team || deleteConfirmText !== 'DELETE') return;
    setIsDeleting(true);
    try {
      // Step 1: Clear assignees from each linked task sequentially
      const tasksToUpdate = (team.tasks || []).filter(
        (task) => task.assigneeUsers?.length > 0 || task.assigneeUser?.id
      );
      for (const task of tasksToUpdate) {
        const taskData = {};
        if (task.assigneeUsers?.length > 0) {
          taskData.assigneeUsers = {
            disconnect: task.assigneeUsers.map((u) => ({ id: u.id })),
          };
        }
        if (task.assigneeUser?.id) {
          taskData.assigneeUser = { disconnect: true };
        }
        await updateTask({ variables: { data: taskData, where: { id: task.id } } });
      }

      // Step 2: Soft-delete (rename to __deleted__) and disconnect all relations
      const teamData = { name: `__deleted__${team.name}` };
      if (team.tasks?.length > 0) {
        teamData.tasks = { disconnect: team.tasks.map((t) => ({ id: t.id })) };
      }
      if (team.members?.length > 0) {
        teamData.members = { disconnect: team.members.map((m) => ({ id: m.id })) };
      }
      if (team.subscribers?.length > 0) {
        teamData.subscribers = { disconnect: team.subscribers.map((s) => ({ id: s.id })) };
      }
      if (team.subscribedTeams?.length > 0) {
        teamData.subscribedTeams = { disconnect: team.subscribedTeams.map((t) => ({ id: t.id })) };
      }
      if (team.subscriberTeams?.length > 0) {
        teamData.subscriberTeams = { disconnect: team.subscriberTeams.map((t) => ({ id: t.id })) };
      }
      if (team.leader) {
        teamData.leader = { disconnect: true };
      }
      await updateTeam({ variables: { data: teamData, where: { id: teamId } } });

      message.success('Team deleted successfully');
      setIsDeleteModalVisible(false);
      setDeleteConfirmText('');
      setIsSettingsModalVisible(false);
      history.push('/tasks/admin');
    } catch (error) {
      message.error('Failed to delete team: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Members table columns for settings modal
  const memberColumns = [
    {
      title: 'Member',
      key: 'member',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AvatarS3
            imgKey={record.avatar?.key}
            username={record.name || record.username}
            avatarProps={{ size: 32 }}
          />
          <div>
            <div style={{ fontWeight: 500, color: text.primary }}>
              {startCase(record.name || record.username)}
            </div>
            {record.username && record.name && (
              <div style={{ fontSize: 12, color: text.secondary }}>
                @{record.username}
              </div>
            )}
          </div>
          {editingTeam?.leader?.id === record.id && (
            <Tag color="gold" style={{ marginLeft: 4 }}>
              <CrownOutlined /> Leader
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      align: 'right',
      render: (_, record) => {
        const isLeader = editingTeam?.leader?.id === record.id;
        const displayName = startCase(record.name || record.username);
        const userTaskCount = allTasks.filter(
          (t) =>
            t.assigneeUsers?.some((u) => u.id === record.id) ||
            t.assigneeUser?.id === record.id
        ).length;
        const removeTitle =
          userTaskCount > 0
            ? `Remove ${displayName} from team? They will also be unassigned from ${userTaskCount} task${userTaskCount === 1 ? '' : 's'} in this team.`
            : `Remove ${displayName} from team?`;
        return (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            {!isLeader && (
              <Popconfirm
                title={`Transfer leadership to ${displayName}?`}
                onConfirm={() => handleTransferLeadership(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Transfer leadership">
                  <Button type="text" icon={<SwapOutlined />} size="small" />
                </Tooltip>
              </Popconfirm>
            )}
            {!isLeader && (
              <Popconfirm
                title={removeTitle}
                onConfirm={() => handleRemoveMember(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Remove from team">
                  <Button type="text" icon={<UserDeleteOutlined />} danger size="small" />
                </Tooltip>
              </Popconfirm>
            )}
            {isLeader && (
              <Tag color="default" style={{ margin: 0 }}>Current leader</Tag>
            )}
          </div>
        );
      },
    },
  ];

  // ============================================
  // RENDER
  // ============================================

  if (teamsLoading && !team) {
    return <AppOverlaySpinner />;
  }

  if (!teamId) {
    return (
      <div className="project-details__container" style={{ paddingInline: isMobile ? 0 : '5%' }}>
        <div style={{ textAlign: 'center', padding: space[10], color: text.muted }}>
          Select a team to view details.
        </div>
      </div>
    );
  }

  return (
    <div className="project-details__container" style={{ paddingInline: isMobile ? 0 : '5%' }}>
      {/* Header */}
      <TeamDetailsHeader
        team={team}
        onBack={() => history.goBack()}
        onSettingsClick={handleOpenSettings}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Stats */}
      <TeamDetailsStats team={team} tasks={allTasks} />

      {/* Members Preview */}
      <div style={{ margin: `${space[4]}px 0` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[2] }}>
          <UserOutlined style={{ marginRight: 6 }} /> Members ({team?.members?.length || 0})
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2] }}>
          {team?.members?.map((member) => (
            <Tooltip key={member.id} title={startCase(member.name || member.username)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: `${space[1]}px ${space[2]}px`, background: neutral[50], borderRadius: radius.md }}>
                <AvatarS3
                  imgKey={member.avatar?.key}
                  username={member.name || member.username}
                  avatarProps={{ size: 24 }}
                />
                <span style={{ fontSize: 13, fontWeight: 500 }}>
                  {startCase(member.name || member.username)}
                </span>
                {team?.leader?.id === member.id && (
                  <CrownOutlined style={{ color: '#faad14', fontSize: 12 }} />
                )}
              </div>
            </Tooltip>
          ))}
        </div>
      </div>

      {/* Subscriptions — outgoing only */}
      {team?.subscribedTeams?.length > 0 && (
        <div style={{ marginBottom: space[4] }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: space[2] }}>
            <span style={{ fontSize: 12, color: text.label }}>Subscribed to</span>
            <Tooltip title="Other teams that this team follows. When those teams get new tasks, this team's members are auto-added as collaborators.">
              <InfoCircleOutlined style={{ fontSize: 12, color: text.muted, cursor: 'help' }} />
            </Tooltip>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {team.subscribedTeams.map((t) => (
              <Tag key={t.id} color="blue" style={{ fontSize: 12 }}>{t.name}</Tag>
            ))}
          </div>
        </div>
      )}

      {/* Subscribers — incoming (who follows this team) */}
      <div style={{ marginBottom: space[4] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: space[2] }}>
          <span style={{ fontSize: 12, color: text.label }}>
            Subscribers ({(team?.subscribers?.length || 0) + (team?.subscriberTeams?.length || 0)})
          </span>
          <Tooltip title="Users and teams that follow this team. They are auto-added as collaborators when new tasks are created for this team, so they get notified about its work.">
            <InfoCircleOutlined style={{ fontSize: 12, color: text.muted, cursor: 'help' }} />
          </Tooltip>
        </div>

        {(team?.subscribers?.length > 0 || team?.subscriberTeams?.length > 0) ? (
          <>
            {/* Individual subscribers */}
            {team?.subscribers?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space[2], marginBottom: team?.subscriberTeams?.length > 0 ? space[2] : 0 }}>
                {team.subscribers.map((sub) => (
                  <Tooltip
                    key={sub.id}
                    title={`${startCase(sub.name || sub.username)} — individual subscriber. Auto-added as a collaborator on new team tasks.`}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: `${space[1]}px ${space[2]}px`,
                        background: '#f5f3ff',
                        border: '1px solid #e9d5ff',
                        borderRadius: radius.md,
                      }}
                    >
                      <AvatarS3
                        imgKey={sub.avatar?.key}
                        username={sub.name || sub.username}
                        avatarProps={{ size: 22 }}
                      />
                      <span style={{ fontSize: 12, fontWeight: 500 }}>
                        {startCase(sub.name || sub.username)}
                      </span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}

            {/* Subscriber teams */}
            {team?.subscriberTeams?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {team.subscriberTeams.map((t) => {
                  const memberCount = t.members?.length || 0;
                  return (
                    <Tooltip
                      key={t.id}
                      title={`Team "${t.name}" subscribed via team. ${memberCount} member${memberCount === 1 ? '' : 's'} get notified as collaborators on new tasks.`}
                    >
                      <Tag color="blue" style={{ fontSize: 12, cursor: 'help' }}>
                        <TeamOutlined /> {t.name} ({memberCount})
                      </Tag>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <span style={{ fontSize: 12, color: text.muted, fontStyle: 'italic' }}>
            No one is subscribed to this team yet.
          </span>
        )}
      </div>

      <Divider style={{ margin: `${space[3]}px 0` }} />

      {/* Tabs */}
      <Tabs
        defaultActiveKey="tasks"
        className="task-tabs-details-history"
        size={isMobile ? 'small' : 'default'}
      >
        <TabPane
          tab={
            <span>
              <TeamOutlined /> Tasks ({allTasks.length})
            </span>
          }
          key="tasks"
        >
          <TeamTasksList
            tasks={allTasks}
            members={team?.members || []}
            loading={allTasksLoading}
          />
        </TabPane>
        <TabPane
          tab={
            <span>
              <InboxOutlined /> Unassigned ({unassignedTasks.length})
            </span>
          }
          key="unassigned"
        >
          <TeamTasksList
            tasks={unassignedTasks}
            members={team?.members || []}
            loading={allTasksLoading}
            emptyMessage="No unassigned tasks for this team"
          />
        </TabPane>
      </Tabs>

      {/* Settings Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ color: '#1890ff' }} />
            <span>Team Settings</span>
          </div>
        }
        visible={isSettingsModalVisible}
        onCancel={() => {
          setIsSettingsModalVisible(false);
          setEditingTeam(null);
          settingsForm.resetFields();
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsSettingsModalVisible(false);
              setEditingTeam(null);
              settingsForm.resetFields();
            }}
          >
            Close
          </Button>,
        ]}
        width={700}
      >
        <Form form={settingsForm} layout="vertical">
          <Form.Item
            name="name"
            label="Team Name"
            rules={[
              { required: true, message: 'Team name is required' },
              {
                validator: (_, value) => {
                  if (
                    value &&
                    allOrgTeams.some(
                      (t) => t.id !== teamId && t.name.toLowerCase() === value.trim().toLowerCase()
                    )
                  ) {
                    return Promise.reject(new Error('A team with this name already exists'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Brief description of this team" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              onClick={handleSettingsSave}
              loading={updateTeamLoading}
            >
              Save Details
            </Button>
          </div>
        </Form>

        {editingTeam && (
          <>
            {/* Leader display */}
            <div style={{ marginBottom: space[4] }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: text.primary, marginBottom: space[2] }}>
                <CrownOutlined style={{ color: '#faad14', marginRight: 6 }} /> Team Leader
              </div>
              {editingTeam.leader ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: `${space[2]}px ${space[3]}px`, background: neutral[50], borderRadius: radius.md }}>
                  <AvatarS3
                    imgKey={editingTeam.leader.avatar?.key}
                    username={editingTeam.leader.name || editingTeam.leader.username}
                    avatarProps={{ size: 32 }}
                  />
                  <span style={{ fontWeight: 500 }}>
                    {startCase(editingTeam.leader.name || editingTeam.leader.username)}
                  </span>
                </div>
              ) : (
                <span style={{ color: text.muted }}>No leader assigned</span>
              )}
            </div>

            <Divider style={{ margin: `${space[3]}px 0` }} />

            {/* Members table */}
            <div style={{ marginBottom: space[3] }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space[2] }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: text.primary }}>
                  <UserOutlined style={{ marginRight: 6 }} /> Members ({editingTeam.members?.length || 0})
                </div>
                <Button
                  size="small"
                  type="primary"
                  icon={<UserOutlined />}
                  onClick={() => {
                    setSelectedMemberIds([]);
                    fetchUsersForOrg();
                    setIsAddMemberModalVisible(true);
                  }}
                >
                  Add Members
                </Button>
              </div>

              <Table
                dataSource={editingTeam.members || []}
                columns={memberColumns}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{ emptyText: 'No members yet' }}
              />
            </div>

            <Divider style={{ margin: `${space[4]}px 0 ${space[3]}px` }} />

            {/* Danger Zone */}
            <div
              style={{
                border: '1px solid #ffccc7',
                borderRadius: radius.md,
                padding: space[3],
                background: '#fff2f0',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#cf1322', marginBottom: space[1] }}>
                <DeleteOutlined style={{ marginRight: 6 }} /> Danger Zone
              </div>
              <div style={{ fontSize: 12, color: text.secondary, marginBottom: space[3] }}>
                Deleting this team will unassign all of its tasks, remove every member and subscriber, and disconnect all team-to-team subscriptions. This cannot be undone from the UI.
              </div>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setDeleteConfirmText('');
                  setIsDeleteModalVisible(true);
                }}
              >
                Delete Team
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* Add Members Modal */}
      <Modal
        title="Add Members to Team"
        visible={isAddMemberModalVisible}
        onOk={handleAddMembers}
        onCancel={() => {
          setIsAddMemberModalVisible(false);
          setSelectedMemberIds([]);
        }}
        confirmLoading={updateTeamLoading}
        okText={`Add ${selectedMemberIds.length > 0 ? `(${selectedMemberIds.length})` : ''}`}
        width={500}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          placeholder="Search and select members to add"
          style={{ width: '100%' }}
          value={selectedMemberIds}
          onChange={setSelectedMemberIds}
          loading={basicUsersDetailsLoading}
          onSearch={(searchText) => fetchUsersForOrg(searchText)}
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {basicUsersDetails
            .filter((user) => !editingTeam?.members?.some((m) => m.id === user.id))
            .map((user) => (
              <Option key={user.id} value={user.id}>
                {startCase(user.name || user.username)}
              </Option>
            ))}
        </Select>
      </Modal>

      {/* Delete Team Modal — typed confirmation */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#cf1322' }}>
            <DeleteOutlined />
            <span>Delete Team</span>
          </div>
        }
        visible={isDeleteModalVisible}
        onCancel={() => {
          if (isDeleting) return;
          setIsDeleteModalVisible(false);
          setDeleteConfirmText('');
        }}
        maskClosable={!isDeleting}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsDeleteModalVisible(false);
              setDeleteConfirmText('');
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={isDeleting}
            disabled={deleteConfirmText !== 'DELETE'}
            onClick={handleDeleteTeam}
          >
            Delete Team
          </Button>,
        ]}
        width={500}
      >
        <div style={{ marginBottom: space[3] }}>
          You are about to delete <strong>{team?.name}</strong>. This action will:
        </div>
        <ul style={{ marginBottom: space[3], color: text.secondary, paddingLeft: space[5] }}>
          <li>Unassign all tasks currently linked to this team</li>
          <li>Remove all {team?.members?.length || 0} member{(team?.members?.length || 0) === 1 ? '' : 's'} and any subscribers</li>
          <li>Disconnect all team-to-team subscriptions</li>
        </ul>
        <div style={{ marginBottom: space[2], fontSize: 13 }}>
          Type <strong>DELETE</strong> to confirm.
        </div>
        <Input
          placeholder="DELETE"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          onPressEnter={() => {
            if (deleteConfirmText === 'DELETE' && !isDeleting) handleDeleteTeam();
          }}
          autoFocus
          disabled={isDeleting}
        />
      </Modal>
    </div>
  );
};

export default withRouter(TeamDetails);
